import DashboardImg from '@/assets/images/background/dashboard.png';
import Logo from '@/components/logo';
import SettingButton from '@/layouts/components/setting-button';
import { Activity, Hotel, ShieldCheck, Sparkles } from 'lucide-react';
import LoginForm from './login-form';

function LoginPage() {
  return (
    <div className="relative min-h-svh overflow-hidden bg-[#f6f8fb] text-[#18212f] dark:bg-[#0b0f14] dark:text-white">
      <div className="absolute inset-x-0 top-0 h-48 bg-[linear-gradient(180deg,rgba(66,184,131,0.16),rgba(246,248,251,0))] dark:bg-[linear-gradient(180deg,rgba(66,184,131,0.16),rgba(11,15,20,0))]" />
      <div className="relative grid min-h-svh lg:grid-cols-[0.92fr_1.08fr]">
        <div className="flex flex-col px-6 py-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5 dark:bg-white/10 dark:ring-white/10">
                <Logo size={28} />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#1f8f66]">FDTV+</div>
                <div className="text-xs font-medium text-[#667085] dark:text-white/55">
                  Admin Console
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center py-12">
            <div className="w-full max-w-[420px]">
              <div className="mb-8 space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#247a5c] shadow-sm ring-1 ring-black/5 dark:bg-white/10 dark:text-[#75e0b2] dark:ring-white/10">
                  <ShieldCheck className="size-3.5" />
                  Secure admin access
                </div>
                <h1 className="text-4xl font-semibold leading-tight text-[#121926] dark:text-white">
                  Welcome to FDTV Admin.
                </h1>
                <p className="max-w-sm text-sm leading-6 text-[#667085] dark:text-white/60">
                  Sign in to access the FDTV administrative workspace.
                </p>
              </div>
              <div className="rounded-2xl bg-white p-6 shadow-[0_24px_70px_rgba(16,24,40,0.12)] ring-1 ring-black/5 dark:bg-white/[0.07] dark:shadow-black/30 dark:ring-white/10">
                <LoginForm />
              </div>
            </div>
          </div>
        </div>

        <div className="relative hidden min-h-svh overflow-hidden bg-[#12201b] p-10 lg:block">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(32,118,83,0.9),rgba(18,32,27,0.96)_55%,rgba(11,15,20,1))]" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)] [background-size:48px_48px]" />
          <div className="relative flex h-full flex-col justify-between">
            <div className="flex justify-end gap-3">
              <div className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white/80 ring-1 ring-white/15">
                Live Operations
              </div>
              <div className="rounded-full bg-[#75e0b2]/20 px-4 py-2 text-xs font-semibold text-[#a9f4d2] ring-1 ring-[#75e0b2]/25">
                Admin Ready
              </div>
            </div>

            <div className="mx-auto w-full max-w-[680px]">
              <div className="mb-7 grid grid-cols-3 gap-3">
                {[
                  { label: 'Properties', value: 'Centralized', icon: Hotel },
                  { label: 'Activity', value: 'Realtime', icon: Activity },
                  { label: 'Access', value: 'Protected', icon: ShieldCheck },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl bg-white/[0.09] p-4 text-white shadow-2xl ring-1 ring-white/12 backdrop-blur"
                  >
                    <item.icon className="mb-3 size-5 text-[#75e0b2]" />
                    <div className="text-sm font-semibold">{item.value}</div>
                    <div className="mt-1 text-xs text-white/50">{item.label}</div>
                  </div>
                ))}
              </div>

              <div className="relative rounded-[28px] bg-white/[0.09] p-5 shadow-[0_32px_90px_rgba(0,0,0,0.36)] ring-1 ring-white/15 backdrop-blur">
                <div className="rounded-[22px] bg-[#f8fbfa] p-6">
                  <div className="mb-5 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase text-[#1f8f66]">
                        FDTV+ Admin
                      </div>
                      <div className="mt-1 text-xl font-semibold text-[#18212f]">
                        Operations Overview
                      </div>
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-[#e8f8f0] px-3 py-1.5 text-xs font-semibold text-[#247a5c]">
                      <Sparkles className="size-3.5" />
                      Healthy
                    </div>
                  </div>
                  <img
                    src={DashboardImg}
                    alt="FDTV+ admin dashboard preview"
                    className="mx-auto aspect-square w-full max-w-[420px] object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="max-w-xl">
              <h2 className="text-3xl font-semibold leading-tight text-white">
                A secure admin console for FDTV.
              </h2>
              <p className="mt-3 text-sm leading-6 text-white/58">
                Authorized users can continue to the FDTV management workspace.
              </p>
            </div>
          </div>
        </div>

        <div className="absolute right-2 top-0 flex flex-row">
          <SettingButton />
        </div>
      </div>
    </div>
  );
}
export default LoginPage;
